import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '@/stores/auth.store';

function getToken(): string | null {
  const storeToken = useAuthStore.getState().getAccessToken();
  if (storeToken) return storeToken;
  const directToken = useAuthStore.getState().accessToken;
  if (directToken) return directToken;
  return localStorage.getItem('token');
}

// CommandStateMessage matches the backend envelope at
// internal/pkg/websocket/messages.go. State values come from
// internal/domain/command_state.go.
export interface CommandStateMessage {
  type: 'command_state';
  command_id: string;
  device_id: string;
  tenant_id: string;
  command_type: string;
  state: 'queued' | 'dispatched' | 'executing' | 'waiting_user' | 'succeeded' | 'failed' | 'timeout' | 'cancelled';
  previous_state?: string;
  state_history?: Array<{ state: string; at: string; detail?: string }>;
  initiator?: { user_id?: string; source?: string; automation_id?: string };
  result?: unknown;
  error_code?: number;
  error_reason?: string;
  late_ack_received_at?: string;
  version: number;
  is_late_ack?: boolean;
}

interface UseCommandStreamOptions {
  deviceId?: string;
  enabled?: boolean;
}

interface UseCommandStreamReturn {
  // Latest command_state envelope per command_id, keyed by command_id.
  // Re-renders when any subscribed command transitions state.
  commands: Map<string, CommandStateMessage>;
  isConnected: boolean;
  error: Error | null;
  reconnect: () => void;
}

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/api/v1`;

// useCommandStream subscribes to /ws/devices/:id/commands and exposes
// a Map<command_id, CommandStateMessage> of the latest state per
// command. Consumers index by command_id to read a specific command's
// state (e.g., the in-flight screenshot capture).
//
// Frontend equivalent of the WS broadcaster from the Reaper spec —
// each command_state transition lands here within ~ms of the backend
// write. Drops are detected via the monotonic `version` field; the
// caller is responsible for refetching via REST when a gap appears.
export function useCommandStream({
  deviceId,
  enabled = true,
}: UseCommandStreamOptions): UseCommandStreamReturn {
  const [commands, setCommands] = useState<Map<string, CommandStateMessage>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const isUnmountedRef = useRef(false);
  const connectRef = useRef<() => void>(() => {});

  const connect = useCallback(() => {
    if (!enabled || !deviceId) return;

    const accessToken = getToken();
    if (!accessToken) return;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const apiUrl = new URL(API_BASE);
    const wsBase = `${wsProtocol}//${apiUrl.host}${apiUrl.pathname}`;
    const wsUrl = `${wsBase}/ws/devices/${deviceId}/commands`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'auth', token: accessToken }));
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const messages = (event.data as string).split('\n').filter(Boolean);
          // Buffer all incoming envelopes into one state update — the WS
          // hub can deliver several in one frame during burst transitions
          // (queued→dispatched→succeeded for FAF commands lands in <50ms).
          // Functional setState avoids capturing a stale `commands` from
          // the connect() closure.
          const updates: CommandStateMessage[] = [];
          for (const msgStr of messages) {
            const msg = JSON.parse(msgStr) as CommandStateMessage | Record<string, unknown>;
            if ((msg as CommandStateMessage).type !== 'command_state') continue;
            updates.push(msg as CommandStateMessage);
          }
          if (updates.length === 0) return;
          setCommands((prev) => {
            const next = new Map(prev);
            for (const cmd of updates) {
              // Only accept newer versions — out-of-order delivery shouldn't
              // regress the visible state. (Backend hub preserves order
              // per-device, but defense-in-depth.)
              const existing = next.get(cmd.command_id);
              if (existing && existing.version > cmd.version) continue;
              next.set(cmd.command_id, cmd);
            }
            return next;
          });
        } catch (e) {
          console.error('[CommandStream] Failed to parse WebSocket message:', e);
        }
      };

      ws.onerror = (event) => {
        console.error('[CommandStream] WebSocket error:', event, 'readyState:', ws.readyState, 'url:', ws.url);
        setError(new Error('WebSocket connection error'));
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        wsRef.current = null;
        if (isUnmountedRef.current) return;
        const intentionalClose = event.code === 1000;
        if (enabled && !intentionalClose) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            connectRef.current();
          }, delay);
        }
      };
    } catch (e) {
      console.error('[CommandStream] Exception creating WebSocket:', e);
      setError(e as Error);
    }
  }, [enabled, deviceId]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;
    connect();
  }, [connect]);

  useEffect(() => {
    isUnmountedRef.current = false;
    connect();
    return () => {
      isUnmountedRef.current = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'unmount');
        wsRef.current = null;
      }
    };
  }, [connect]);

  return { commands, isConnected, error, reconnect };
}
